import {keyBy, uniqueId} from 'lodash';
import React, {FC, useCallback, useEffect, useReducer} from 'react';

import {addQueryToPath} from '../../../../../../libs/shared/helpers/src/browser/urlHelpers';
import {Trans} from '../../../../../../libs/shared/helpers/src/internationalization/react';
import {
  useBuildCancelToken,
  usePrevious,
} from '../../../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {CancelError} from '../../../../../../libs/shared/helpers/src/utilities/asyncCancelHelpers';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {reportToBugsnag} from '../../../../../../srcCommon/services/bugsnag/bugsnag';
import {useAppSelector} from '../../../../../helpers/reduxHelpers';
import {isContributingSegmentsOptionsWithSubMetric} from '../../../../../models/api/analyticsMetricDetailsModel';
import {AnalyticsMetricsKeys} from '../../../../../models/api/analyticsMetricsModel';
import {AnalyticsReportFilterOptions} from '../../../../../models/api/analyticsSavedViewModel';
import {
  AnalyticsTimelineEvent,
  AnalyticsTimelineEventsEnum,
} from '../../../../../models/api/analyticsTimelineEventModel';
import {computeShownEventsForMetric} from '../../../../../models/api/analyticsTimelineMetricShownEvents';
import {CardModel} from '../../../../../models/api/cardModel';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {ApiAnalyticsActivityPageDataGroup} from '../../../../../models/api/dataGroupModel';
import {MessageModel} from '../../../../../models/api/messageModel';
import {fetchApiAsync} from '../../../../../services/http/http';
import {HttpError} from '../../../../../services/http/httpError';
import {ResourceMap} from '../../../../../store/appTypes';
import {getCompany} from '../../../../../store/data/company/companySelectors';
import {
  getAnalyticsDetailsSelectedMetricWithOptions,
  getSelectedConversation,
} from '../../../../../store/panels/analytics/details/analyticsPanelDetailsSelectors';
import {
  ContributingSegmentsOptionsEnum,
  ReportFilterOptionsEnum,
} from '../../../../helpers/analyticsReportHelpers';
import {SidePanelSectionWrapper} from '../sidePanelSectionWrapper';
import {TimelineEditSortOrder, TimelineSortOrdersEnum} from './timelineEditSortOrder';
import {TimelineEventsLoading} from './timelineEventsLoading';
import {TimelineEventsSection} from './timelineEventsSection';

/*
 * Constants.
 */

// We don't consider all activities, and the loading is manual, hence the high page size.
const timelinePageSize = 100;

const defaultState: State = {
  timelineEvents: [],
  eventSections: [],
  eventSectionsVisibilityMap: {},
  messagesResourcesMap: {},
  cardsResourcesMap: {},
  isLoading: false,
  hasError: false,
  pageBoundaryDate: 0,
  sortOrder: TimelineSortOrdersEnum.OLDEST_FIRST,
  areAllEventsLoaded: false,
};

// We always want to show messages in the timeline.
const alwaysShownEvents = [
  AnalyticsTimelineEventsEnum.INBOUND,
  AnalyticsTimelineEventsEnum.OUT_REPLY,
  AnalyticsTimelineEventsEnum.OUTBOUND,
];

/*
 * Types.
 */

type State = {
  timelineEvents: ReadonlyArray<AnalyticsTimelineEvent>;
  eventSections: ReadonlyArray<TimelineEventsSection>;
  // A map from the string ID of each events section to a boolean
  // indicating whether or not that section of events is currently visible.
  eventSectionsVisibilityMap: Record<string, boolean>;
  messagesResourcesMap: ResourceMap<MessageModel>;
  cardsResourcesMap: ResourceMap<CardModel>;
  isLoading: boolean;
  hasError: boolean;
  pageBoundaryDate: number;
  sortOrder: TimelineSortOrdersEnum;
  areAllEventsLoaded: boolean;
};

enum ActionTypesEnum {
  FETCH_STARTED = 'fetch-started',
  FETCH_COMPLETED = 'fetch-completed',
  FETCH_FAILED = 'fetch-failed',
  CONVERSATION_CHANGED = 'conversation-changed',
  SORT_ORDER_CHANGED = 'sort-order-changed',
  SHOW_HIDDEN_EVENTS_SECTION = 'show-hidden-events-section',
}

type Action =
  | {type: ActionTypesEnum.FETCH_STARTED}
  | {type: ActionTypesEnum.FETCH_FAILED}
  | {
      type: ActionTypesEnum.FETCH_COMPLETED;
      value: {
        events: ReadonlyArray<AnalyticsTimelineEvent>;
        eventSections: ReadonlyArray<TimelineEventsSection>;
        eventSectionVisibility: Record<string, boolean>;
        messagesResourcesMap: ResourceMap<MessageModel>;
        cardsResourcesMap: ResourceMap<CardModel>;
        pageBoundaryDate?: number;
      };
    }
  | {type: ActionTypesEnum.CONVERSATION_CHANGED}
  | {type: ActionTypesEnum.SORT_ORDER_CHANGED; value: {sortOrder: TimelineSortOrdersEnum}}
  | {type: ActionTypesEnum.SHOW_HIDDEN_EVENTS_SECTION; value: {id: string}};

/*
 * Component.
 */

export const TimelineSection: FC = () => {
  const company = useAppSelector(getCompany);
  const selectedConversation = useAppSelector(getSelectedConversation);
  const {selectedMetric, options} = useAppSelector(getAnalyticsDetailsSelectedMetricWithOptions);

  const [
    {
      timelineEvents,
      eventSections,
      eventSectionsVisibilityMap,
      messagesResourcesMap,
      cardsResourcesMap,
      isLoading,
      hasError,
      pageBoundaryDate,
      sortOrder,
      areAllEventsLoaded,
    },
    dispatchAction,
  ] = useReducer(reducer, defaultState);

  const {buildCancelToken} = useBuildCancelToken();

  const fetchConversationTimeline = useCallback(
    async (
      selectedConversationToFetch: ConversationModel | undefined,
      pageBoundaryDateForFetch: number,
      sort: TimelineSortOrdersEnum,
    ) => {
      if (!selectedConversationToFetch) {
        return;
      }

      // Create a new cancel token, canceling any existing one.
      const cancelToken = buildCancelToken();

      // Prepare query with pagination.
      const basePath = getConversationTimelineApiPath(company.url, selectedConversationToFetch.id);
      const query = {
        limit: timelinePageSize,
        sort,
      };

      /*
       * If we are currently sorting by oldest first, then our page boundary
       * date is the date of the newest item we have loaded, and when loading
       * more we want to get things AFTER that time.
       * And vice versa:
       * If we are currently sorting by newest first, then our page boundary
       * date is the date of the oldest item we have loaded, and when loading
       * more we want to get things BEFORE that time.
       */
      const queryWithPaginationDate =
        sort === TimelineSortOrdersEnum.OLDEST_FIRST
          ? {...query, after: pageBoundaryDateForFetch}
          : {...query, before: pageBoundaryDateForFetch};

      const path = addQueryToPath(basePath, queryWithPaginationDate);

      // Fetch.
      dispatchAction({type: ActionTypesEnum.FETCH_STARTED});
      try {
        const {analyticsActivities, messages, cards, timeline} = await fetchApiAsync(
          ApiAnalyticsActivityPageDataGroup,
          path,
          {
            cancelToken,
          },
        );

        const newMessagesResourcesMap = keyBy(messages, (message) => message.uid);
        // Manually building the lookup map for the cards as we want the contactUids to act as keys.
        const newCardsResourcesMap: Record<string, CardModel> = {};
        cards?.forEach((card) =>
          card.contactUids.forEach((contactUid: string) => {
            if (!newCardsResourcesMap[contactUid]) {
              newCardsResourcesMap[contactUid] = card;
            }
          }),
        );

        // If we are sorting oldest first only the bottomBoundary will be defined, and if
        // sorting newest first only the topBoundary will be defined.
        const newPageBoundaryDate =
          timeline?.bottomBoundary?.activityDate ?? timeline?.topBoundary?.activityDate;

        const events = analyticsActivities || [];
        const shownEventTypes = selectedMetric
          ? computeShownEventsForSelectedMetric(selectedMetric, options)
          : [];

        const {timelineEventSections, timelineEventSectionVisibility} = computeEventSections(
          events,
          shownEventTypes.concat(alwaysShownEvents),
        );

        dispatchAction({
          type: ActionTypesEnum.FETCH_COMPLETED,
          value: {
            events,
            eventSections: timelineEventSections,
            eventSectionVisibility: timelineEventSectionVisibility,
            messagesResourcesMap: newMessagesResourcesMap,
            cardsResourcesMap: newCardsResourcesMap,
            pageBoundaryDate: newPageBoundaryDate,
          },
        });
      } catch (error) {
        if (error instanceof CancelError) {
          return;
        }

        if (!(error instanceof HttpError)) {
          reportToBugsnag(error);
        }

        dispatchAction({type: ActionTypesEnum.FETCH_FAILED});
      }
    },
    [company.url, options, selectedMetric, buildCancelToken],
  );

  const showHiddenEvents = (id: string) => {
    dispatchAction({type: ActionTypesEnum.SHOW_HIDDEN_EVENTS_SECTION, value: {id}});
  };

  const previousConversationId = usePrevious(selectedConversation?.id);
  useEffect(() => {
    // Makes sure we don't do the initial fetch again when: `fetchConversationTimeline` is refreshed or the conversation object is updated.
    if (selectedConversation?.id === previousConversationId) {
      return;
    }

    dispatchAction({type: ActionTypesEnum.CONVERSATION_CHANGED});
    fetchConversationTimeline(selectedConversation, pageBoundaryDate, sortOrder);
  }, [selectedConversation, pageBoundaryDate, previousConversationId, fetchConversationTimeline, sortOrder]);

  const handleSelectedSortOrder = async (selectedSortOrder: TimelineSortOrdersEnum) => {
    if (sortOrder === selectedSortOrder) {
      return;
    } // Nothing to do

    dispatchAction({type: ActionTypesEnum.SORT_ORDER_CHANGED, value: {sortOrder: selectedSortOrder}});
    fetchConversationTimeline(selectedConversation, defaultState.pageBoundaryDate, selectedSortOrder);
  };

  if (timelineEvents.length === 0 && !isLoading && !hasError) {
    return null;
  } // TODO: handle no events.

  return (
    <SidePanelSectionWrapper
      sectionTitle={<Trans id="analytics-drawer-details-timeline">Timeline</Trans>}
      isCollapsible
      itemCount={!isLoading && areAllEventsLoaded ? timelineEvents.length : 0}
      headerRightButtons={
        <TimelineEditSortOrder sortOrder={sortOrder} setSortOrder={handleSelectedSortOrder} />
      }
    >
      {hasError && (
        <Trans id="analytics-drawer-details-failed-to-load-timeline">Failed to load timeline</Trans>
      )}
      {eventSections.map((section, index) => (
        <TimelineEventsSection
          key={section.key}
          timelineSection={section}
          messagesResourcesMap={messagesResourcesMap}
          cardsResourcesMap={cardsResourcesMap}
          isVisible={eventSectionsVisibilityMap[section.key]}
          isLastSection={index === eventSections.length - 1}
          showSection={() => showHiddenEvents(section.key)}
        />
      ))}
      {isLoading && <TimelineEventsLoading />}
      {!isLoading && !areAllEventsLoaded && (
        <div style={{display: 'inline-block'}}>
          <Button
            variant={ButtonVariantsEnum.LINK}
            size={VisualSizesEnum.SMALL}
            onClick={() => fetchConversationTimeline(selectedConversation, pageBoundaryDate, sortOrder)}
          >
            <Trans id="analytics-timeline-load-more">Load more</Trans>
          </Button>
        </div>
      )}
    </SidePanelSectionWrapper>
  );
};

/*
 * Reducer.
 */

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case ActionTypesEnum.FETCH_STARTED:
      return {
        ...state,
        isLoading: true,
      };

    case ActionTypesEnum.SORT_ORDER_CHANGED:
      return {
        ...state,
        timelineEvents: [],
        eventSections: [],
        eventSectionsVisibilityMap: {},
        areAllEventsLoaded: false,
        pageBoundaryDate: defaultState.pageBoundaryDate,
        sortOrder: action.value.sortOrder,
      };

    case ActionTypesEnum.FETCH_COMPLETED:
      return {
        ...state,
        hasError: false,
        isLoading: false,
        timelineEvents: [...state.timelineEvents, ...action.value.events],
        eventSections: [...state.eventSections, ...action.value.eventSections],
        eventSectionsVisibilityMap: {
          ...state.eventSectionsVisibilityMap,
          ...action.value.eventSectionVisibility,
        },
        messagesResourcesMap: {...state.messagesResourcesMap, ...action.value.messagesResourcesMap},
        cardsResourcesMap: {...state.cardsResourcesMap, ...action.value.cardsResourcesMap},
        pageBoundaryDate: action.value.pageBoundaryDate ?? 0,
        areAllEventsLoaded: action.value.pageBoundaryDate === undefined,
      };

    case ActionTypesEnum.FETCH_FAILED:
      return {
        ...state,
        isLoading: false,
        hasError: true,
      };

    case ActionTypesEnum.CONVERSATION_CHANGED:
      return defaultState;

    case ActionTypesEnum.SHOW_HIDDEN_EVENTS_SECTION: {
      return {
        ...state,
        eventSectionsVisibilityMap: {...state.eventSectionsVisibilityMap, [action.value.id]: true},
      };
    }
    default:
      return state;
  }
}

/*
 * Helpers.
 */

function getConversationTimelineApiPath(companyUrl: string, conversationId: number) {
  return `${companyUrl}/conversations/${conversationId}/timeline`;
}

function computeShownEventsForSelectedMetric(
  selectedMetric: AnalyticsMetricsKeys,
  options?: AnalyticsReportFilterOptions,
): ReadonlyArray<AnalyticsTimelineEventsEnum> {
  const option = options?.[ReportFilterOptionsEnum.COLLECT_CONTRIBUTING_SEGMENT_IDS];
  const subMetric =
    option && isContributingSegmentsOptionsWithSubMetric(option)
      ? option[ContributingSegmentsOptionsEnum.SUB_METRIC]
      : undefined;

  return computeShownEventsForMetric(selectedMetric, subMetric);
}

/**
 * Computes the collapsed sections of the timeline.
 */
function computeEventSections(
  events: ReadonlyArray<AnalyticsTimelineEvent>,
  shownEvents: ReadonlyArray<AnalyticsTimelineEventsEnum>,
): {
  timelineEventSections: ReadonlyArray<TimelineEventsSection>;
  timelineEventSectionVisibility: Record<string, boolean>;
} {
  const eventSections: TimelineEventsSection[] = [];
  const sectionsVisibility: Record<string, boolean> = {};

  events.forEach((event, index) => {
    const isLastEventShown = index > 0 ? shownEvents.includes(events[index - 1].type) : undefined;
    const isEventShown = shownEvents.includes(event.type);

    if (isLastEventShown !== isEventShown) {
      // When we go from one shown/hidden status to another, we create a new section.
      const key = uniqueId('timeline-section-');
      eventSections.push({key, events: []});
      sectionsVisibility[key] = isEventShown;
    }

    // We always add an event to the current section (the last one in the array) on each iteration of the loop.
    const updatedEvents = [...eventSections[eventSections.length - 1].events, event];
    eventSections[eventSections.length - 1].events = updatedEvents;
  });

  return {timelineEventSections: eventSections, timelineEventSectionVisibility: sectionsVisibility};
}
